import { SchemaType } from "config/config";
import { ValueLabelPair } from "containers/ExecutionFilter/interfaces";
import { schemaSelector } from "containers/Resources/Resources.selectors";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { sortAlphabetically } from "utils/sorting";
import { capitalizeFirstLetter } from "utils/utils";

/**
 * Enumerate the subrecord relations that the backend `exists` (has-related-record)
 * filter accepts for a given resource tab.
 *
 * The backend resolver (sarest/table/select_mixin.py `_resolve_exists_relation`)
 * iterates the type's `all_tables` — i.e. the table itself plus its parent tables,
 * recursively — and collects each table's `subrecord_tables`. Only those names are
 * valid `relation` values; an `exists` filter naming a `child_tables` entry or a
 * reverse-FK relation is rejected with HTTP 400. So we mirror that exact traversal
 * here: start at `activeTab`, union its `subrecord_tables`, and walk up through
 * `parent_table_names`.
 *
 * Example: for `custom_object` this surfaces `record_metadata`, `git_log`, … (which
 * hang off the `record` parent table), enabling Stefan's original request — "list
 * all records with any record_metadata row matching integrationtest_temp eq True".
 *
 * Returns [] when `activeTab` is undefined (the caller then hides the builder), so
 * threading `activeTab` is what opts a filter surface into the exists builder.
 */
export const useMatchingRelations = ({ activeTab }: { activeTab?: string }): ValueLabelPair[] => {
    const schema: SchemaType[] = useSelector(schemaSelector);

    return useMemo(() => {
        if (!activeTab) {
            return [];
        }
        const byName = new Map(schema.map((table) => [table.name, table]));
        const relations = new Set<string>();
        const visited = new Set<string>();

        const visit = (name?: string) => {
            if (!name || visited.has(name)) {
                return;
            }
            visited.add(name);
            const table = byName.get(name);
            if (!table) {
                return;
            }
            (table.subrecord_tables ?? []).forEach((relation) => relations.add(relation));
            (table.parent_table_names ?? []).forEach(visit);
        };
        visit(activeTab);

        const list = Array.from(relations).map((name) => ({
            value: name,
            label: capitalizeFirstLetter(name.replaceAll("_", " ")),
        }));
        return sortAlphabetically(list, (relation) => relation.label);
    }, [schema, activeTab]);
};
